/** @jsx jsx */
import { Flex, Paragraph } from "@eworkit/iris";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { TimeReportAggregateReturn } from "@/data/time-reports";

import { TimeReportCalendarRow } from "./grid/TimeReportCalendarRow";
import { TimeReportCalendarRowCell } from "./grid/TimeReportCalendarRowCell";
import { TimeReportCalendarRowHeader } from "./grid/TimeReportCalendarRowHeader";

interface TimeReportCalendarSummaryRowProps {
  aggregateData: TimeReportAggregateReturn;
}

export const TimeReportCalendarSummaryRow: FC<TimeReportCalendarSummaryRowProps> = ({ aggregateData }) => {
  const { t } = useTranslation("session");

  const hasHourlyPaycodes = Boolean(aggregateData.aggregateTotalReportedTime.hourly);
  const hasDailyPaycodes = Boolean(aggregateData.aggregateTotalReportedTime.daily);

  return (
    <React.Fragment>
      {aggregateData.aggregateTotalReportedTime.hourly && (
        <TimeReportCalendarRow>
          <TimeReportCalendarRowHeader
            sx={{
              borderBottom:
                hasDailyPaycodes && hasHourlyPaycodes ? theme => `1px solid ${theme.colors?.n0}` : undefined,
            }}>
            <Flex justifyContent="space-between">
              <Paragraph bold ellipsis>
                {t("TIME_REPORTS.CALENDAR.SUMMARY")}
              </Paragraph>
              <Paragraph bold sx={{ display: ["none", null, null, "block"] }}>
                {aggregateData.aggregateTotalReportedTime.hourly.formattedAmount}
              </Paragraph>
            </Flex>
          </TimeReportCalendarRowHeader>

          {aggregateData.days.map(day => (
            <TimeReportCalendarRowCell key={day.date.toISOString()}>
              <Paragraph bold color={day.dayTotalLoggedTime.hourly!.isEmpty ? "n500" : "n1000"}>
                {day.isInTimeReportingPeriod ? day.dayTotalLoggedTime.hourly!.formattedAmount : <span>&nbsp;</span>}
              </Paragraph>
            </TimeReportCalendarRowCell>
          ))}
        </TimeReportCalendarRow>
      )}

      {aggregateData.aggregateTotalReportedTime.daily && (
        <TimeReportCalendarRow>
          <TimeReportCalendarRowHeader>
            <Flex justifyContent="space-between">
              <Paragraph bold ellipsis>
                {hasDailyPaycodes && !hasHourlyPaycodes ? t("TIME_REPORTS.CALENDAR.SUMMARY") : <span>&nbsp;</span>}
              </Paragraph>
              <Paragraph bold sx={{ display: ["none", null, null, "block"] }}>
                {aggregateData.aggregateTotalReportedTime.daily.formattedAmount}
              </Paragraph>
            </Flex>
          </TimeReportCalendarRowHeader>

          {aggregateData.days.map(day => (
            <TimeReportCalendarRowCell key={day.date.toISOString()}>
              <Paragraph bold color={day.dayTotalLoggedTime.daily!.isEmpty ? "n500" : "n1000"}>
                {day.isInTimeReportingPeriod ? day.dayTotalLoggedTime.daily!.formattedAmount : <span>&nbsp;</span>}
              </Paragraph>
            </TimeReportCalendarRowCell>
          ))}
        </TimeReportCalendarRow>
      )}
    </React.Fragment>
  );
};
